import { Component, OnInit, HostListener } from '@angular/core';
import { AnnonceService } from '../annonce.service';
import { ActivatedRoute, Router } from '@angular/router';
import { AwscontrolService } from '../../services/aws/awscontrol.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-annonce-attente-by-id',
  templateUrl: './annonce-attente-by-id.component.html',
  styleUrls: ['./annonce-attente-by-id.component.scss']
})
export class AnnonceAttenteByIdComponent implements OnInit {
  annonce: any;
  id: any;

  // Image principale séparée
  mainImage: string = '';

  // Images supplémentaires (photos sans la principale)
  additionalImages: string[] = [];

  // Toutes les images (pour lightbox)
  allImages: string[] = [];

  // Visionneuse plein écran
  showLightbox = false;
  lightboxIndex = 0;

  // Suivi des téléchargements
  downloading: boolean = false;

  constructor(
    private annonceService: AnnonceService,
    private route: ActivatedRoute,
    public router: Router,
    private awsService: AwscontrolService
  ) { }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');

    this.annonceService.getAnnonceDetails(this.id).subscribe((Response: any) => {
      this.annonce = Response.annonce || Response;
      console.log('📦 Annonce chargée :', this.annonce);
      this.buildImages();
    });
  }

  // ========== ORGANISATION DES IMAGES ==========
  /**
   * Sépare l'image principale des images supplémentaires.
   * L'image principale (Annoncephoto) est mise à part.
   * Les photos du tableau `photos` sont ajoutées à la galerie en excluant l'image principale.
   */
  private buildImages(): void {
    this.mainImage = '';
    this.additionalImages = [];
    this.allImages = [];

    // 1. Image principale
    if (this.annonce?.Annoncephoto) {
      this.mainImage = this.annonce.Annoncephoto;
      this.allImages.push(this.mainImage);
    }

    // 2. Images supplémentaires (sans doublon avec la principale)
    if (this.annonce?.photos && Array.isArray(this.annonce.photos)) {
      this.annonce.photos.forEach((url: string) => {
        if (url && url !== this.mainImage && !this.additionalImages.includes(url)) {
          this.additionalImages.push(url);
          this.allImages.push(url);
        }
      });
    }
  }

  // ========== LIGHTBOX ==========
  openLightbox(index: number): void {
    this.lightboxIndex = index;
    this.showLightbox = true;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox(): void {
    this.showLightbox = false;
    document.body.style.overflow = '';
  }

  nextImage(event?: Event): void {
    if (event) event.stopPropagation();
    if (this.allImages.length === 0) return;
    this.lightboxIndex = (this.lightboxIndex + 1) % this.allImages.length;
  }

  prevImage(event?: Event): void {
    if (event) event.stopPropagation();
    if (this.allImages.length === 0) return;
    this.lightboxIndex = (this.lightboxIndex - 1 + this.allImages.length) % this.allImages.length;
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (!this.showLightbox) return;
    if (event.key === 'Escape') this.closeLightbox();
    if (event.key === 'ArrowRight') this.nextImage();
    if (event.key === 'ArrowLeft') this.prevImage();
  }

  // ========== TÉLÉCHARGEMENT ==========
  async downloadImage(url: string, index?: number): Promise<void> {
    if (!url || this.downloading) return;

    this.downloading = true;
    const fileName = this.getFileNameFromUrl(url) || `image-${(index ?? 0) + 1}.jpg`;

    try {
      const blob = await this.awsService.downloadFile(url).toPromise();

      if (blob instanceof Blob && blob.size > 0) {
        this.saveBlobAsFile(blob, fileName);
        this.showToast(`Image téléchargée : ${fileName}`);
        this.downloading = false;
        return;
      }
      throw new Error('Blob vide');
    } catch (err) {
      console.warn('⚠️ Proxy échoué, tentative fetch :', err);
      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        this.saveBlobAsFile(blob, fileName);
        this.showToast(`Image téléchargée : ${fileName}`);
      } catch (err2) {
        console.error('❌ Fetch échoué :', err2);
        try {
          await this.downloadViaCanvas(url, fileName);
          this.showToast(`Image téléchargée : ${fileName}`);
        } catch (err3) {
          console.error('❌ Canvas échoué :', err3);
          Swal.fire('Erreur', 'Impossible de télécharger cette image.', 'error');
        }
      }
    } finally {
      this.downloading = false;
    }
  }

  private saveBlobAsFile(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
  }

  private downloadViaCanvas(url: string, fileName: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('Canvas non supporté'));
          ctx.drawImage(img, 0, 0);
          canvas.toBlob((blob) => {
            if (!blob) return reject(new Error('Blob vide'));
            this.saveBlobAsFile(blob, fileName);
            resolve();
          }, 'image/jpeg', 0.95);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => reject(new Error('Image non chargeable'));
      img.src = url;
    });
  }

  async downloadAllImages(): Promise<void> {
    if (this.allImages.length === 0) {
      Swal.fire('Aucune image', 'Aucune image à télécharger.', 'info');
      return;
    }

    const confirmed = await Swal.fire({
      title: 'Télécharger toutes les images ?',
      text: `${this.allImages.length} image(s) seront téléchargées sur votre appareil.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, télécharger',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    });

    if (!confirmed.isConfirmed) return;

    Swal.fire({
      title: 'Téléchargement en cours...',
      html: `Progression : <b><span id="dl-progress">0</span> / ${this.allImages.length}</b>`,
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading()
    });

    for (let i = 0; i < this.allImages.length; i++) {
      await this.downloadImageSilent(this.allImages[i], i);
      const progressEl = document.getElementById('dl-progress');
      if (progressEl) progressEl.textContent = String(i + 1);
      await new Promise(res => setTimeout(res, 400));
    }

    Swal.fire({
      title: 'Terminé !',
      text: `${this.allImages.length} image(s) téléchargée(s) sur votre appareil.`,
      icon: 'success',
      timer: 2500,
      showConfirmButton: false
    });
  }

  private async downloadImageSilent(url: string, index: number): Promise<void> {
    const fileName = this.getFileNameFromUrl(url) || `image-${index + 1}.jpg`;

    try {
      const blob = await this.awsService.downloadFile(url).toPromise();
      if (blob instanceof Blob && blob.size > 0) {
        this.saveBlobAsFile(blob, fileName);
        return;
      }
      throw new Error('Blob vide');
    } catch {
      try {
        const response = await fetch(url, { mode: 'cors' });
        if (!response.ok) throw new Error('HTTP error');
        const blob = await response.blob();
        this.saveBlobAsFile(blob, fileName);
      } catch {
        try {
          await this.downloadViaCanvas(url, fileName);
        } catch (err) {
          console.warn('❌ Échec téléchargement :', url, err);
        }
      }
    }
  }

  private getFileNameFromUrl(url: string): string {
    if (!url) return '';
    try {
      const cleanUrl = url.split('?')[0];
      const lastPart = cleanUrl.substring(cleanUrl.lastIndexOf('/') + 1);
      return decodeURIComponent(lastPart);
    } catch {
      return '';
    }
  }

  private showToast(message: string): void {
    Swal.fire({
      title: 'Téléchargé !',
      text: message,
      icon: 'success',
      timer: 1500,
      showConfirmButton: false,
      toast: true,
      position: 'top-end'
    });
  }

  // ========== ACTIONS ==========
  approuveAnnonce() {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Voulez-vous approuver cette annonce ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, approuver !',
      cancelButtonText: 'Annuler'
    }).then((result) => {
      if (result.isConfirmed) {
        this.annonceService.approveAnnouncement(this.id).subscribe(() => {
          Swal.fire('Approuvée !', "L'annonce a été approuvée avec succès.", 'success');
          this.router.navigate(['/annonceAttente']);
        });
      }
    });
  }

  deleteAgencies(id: any) {
    Swal.fire({
      title: 'Êtes-vous sûr(e) ?',
      text: 'Vous ne pourrez pas revenir en arrière !',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimez-le.'
    }).then((result) => {
      if (result.isConfirmed) {
        this.annonceService.deleteItem(id).subscribe(() => {
          Swal.fire('Supprimé!', 'Votre annonce a été supprimée.', 'success');
          this.router.navigate(['/annonceAttente']);
        });
      }
    });
  }

  // ========== UTILITAIRES ==========
  getStatusBadge(status: any): string {
    return status === 'true' || status === true ? 'Approuvée' : 'En attente';
  }

  getStatusIcon(status: any): string {
    return status === 'true' || status === true ? 'check_circle' : 'pending';
  }

  formatPhone(phone: string): string {
    if (!phone) return '';
    return phone.replace(/\s/g, '');
  }
}